import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../lib/auth.js';
import { usePublicBrandingQuery } from '../branding/api.js';
import { useUnreadCountQuery } from '../notifications/api.js';
import { useDashboardQuery } from '../dashboard/api.js';
import { MobileBottomNav, avatarColor, initialsOf } from './MobileNav.js';
import { QuickLogSheet } from './QuickLogSheet.js';

function formatMoneyShort(value: string): string {
  const n = Number(value);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function formatDueTime(iso: string): { primary: string; secondary: string } {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const [primary, secondary] = time.split(' ');
  return { primary: primary ?? time, secondary: secondary ?? '' };
}

export function MobileDashboardPage() {
  const { session } = useAuth();
  const { data: branding } = usePublicBrandingQuery();
  const { data: unread } = useUnreadCountQuery();
  const { data: dashboard, isLoading } = useDashboardQuery('me', 30);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const now = new Date();
  const firstName = session?.user.displayName.split(' ')[0] ?? '';
  const dateLabel = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const oldestOverdue = dashboard?.needsAttention.find((t) => t.isOverdue);
  const trendUp = (dashboard?.totalLeads.deltaPct ?? 0) >= 0;

  return (
    <div className="flex h-dvh flex-col bg-ground text-ink">
      <header className="flex h-14 shrink-0 items-center gap-2.5 px-4 pr-1.5">
        <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-night-surface">
          {branding?.logoDarkUrl || branding?.logoUrl ? (
            <img src={branding.logoDarkUrl ?? branding.logoUrl!} alt={branding.productName} className="size-full object-contain p-0.5" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
              <circle cx="12" cy="12" r="2" fill="#F2B25C" />
              <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
              <circle cx="5" cy="5" r="1.1" fill="#8A909C" />
              <circle cx="19" cy="19" r="1.1" fill="#8A909C" />
            </svg>
          )}
        </span>
        <span className="flex-1 text-[15px] font-[650] tracking-[-0.01em]">{branding?.workspaceName ?? 'Workspace'}</span>
        <button type="button" aria-label="Search" className="flex size-11 items-center justify-center rounded-control">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </button>
        <Link to="/notifications" aria-label={unread?.count ? `Notifications, ${unread.count} unread` : 'Notifications'} className="relative flex size-11 items-center justify-center rounded-control">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
          </svg>
          {!!unread?.count && <span className="absolute right-2.5 top-2.5 size-2 rounded-full border-2 border-ground bg-danger" />}
        </Link>
        <button type="button" aria-label="Account" className="flex size-11 items-center justify-center">
          <span className="flex size-8 items-center justify-center rounded-full text-xs font-[650] text-white" style={{ background: avatarColor(session?.user.displayName ?? '?') }}>
            {initialsOf(session?.user.displayName ?? '?')}
          </span>
        </button>
      </header>

      <main className="flex flex-1 flex-col gap-5 overflow-hidden px-4 pb-4 pt-3">
        <div className="flex flex-col gap-1">
          <h1 className="font-sans m-0 text-[28px] font-normal leading-8 tracking-[-0.01em]">
            Good morning, {firstName}
          </h1>
          <span className="text-[13px] text-text-muted">{dateLabel}</span>
        </div>

        {!isLoading && dashboard && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/m/leads" className="flex flex-col gap-1 rounded-control border border-border bg-surface p-3.5 no-underline shadow-card">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Tasks due</span>
                <span className="text-[26px] font-[650] leading-8 tracking-[-0.02em] tabular-nums">{dashboard.tasksDueToday}</span>
                <span className="text-xs text-text-muted">Today</span>
              </Link>
              <Link to="/m/leads" className="flex flex-col gap-1 rounded-control border border-border bg-surface p-3.5 no-underline shadow-card">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-danger">
                  <span className="size-1.5 rounded-full bg-danger" />
                  Overdue
                </span>
                <span className="text-[26px] font-[650] leading-8 tracking-[-0.02em] tabular-nums text-danger">{dashboard.tasksOverdue}</span>
                <span className="text-xs text-text-muted">{oldestOverdue ? `Oldest from ${new Date(oldestOverdue.dueAt!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '—'}</span>
              </Link>
              <Link to="/m/leads" className="flex flex-col gap-1 rounded-control border border-border bg-surface p-3.5 no-underline shadow-card">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">New leads</span>
                <span className="text-[26px] font-[650] leading-8 tracking-[-0.02em] tabular-nums">{dashboard.newLeads}</span>
                <span className={`text-xs font-semibold ${trendUp ? 'text-success' : 'text-danger'}`}>
                  {trendUp ? '▲' : '▼'} {Math.abs(dashboard.totalLeads.deltaPct ?? 0)}% <span className="font-normal text-text-muted">30 days</span>
                </span>
              </Link>
              <Link to="/m/leads" className="flex flex-col gap-1 rounded-control border border-border bg-surface p-3.5 no-underline shadow-card">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Pipeline</span>
                <span className="text-[26px] font-[650] leading-8 tracking-[-0.02em] tabular-nums">{formatMoneyShort(dashboard.openPipelineValue.value)}</span>
                <span className="text-xs text-text-muted">{dashboard.openDeals} open opportunities</span>
              </Link>
            </div>

            <section aria-labelledby="next-up" className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="flex h-6 items-center justify-between">
                <h2 id="next-up" className="m-0 text-[16px] font-semibold">
                  Next up <span className="text-[13px] font-medium text-text-muted">· {dashboard.needsAttention.length} follow-ups</span>
                </h2>
                <Link to="/m/leads" className="text-sm font-semibold no-underline">
                  See all
                </Link>
              </div>
              <ul className="m-0 flex-1 overflow-y-auto rounded-control border border-border bg-surface p-0 shadow-card" style={{ listStyle: 'none' }}>
                {dashboard.needsAttention.map((task, i) => {
                  const time = task.dueAt ? formatDueTime(task.dueAt) : null;
                  const name = task.relatedPersonName ?? task.relatedName ?? task.title;
                  const rowInner = (
                    <>
                      <span className="flex w-13 shrink-0 flex-col gap-0.5">
                        {task.isOverdue ? (
                          <>
                            <span className="text-xs font-[650] text-danger">Overdue</span>
                            <span className="text-[11px] text-text-muted">{task.dueAt ? new Date(task.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-[650] tabular-nums">{time?.primary}</span>
                            <span className="text-[11px] text-text-muted">{time?.secondary}</span>
                          </>
                        )}
                      </span>
                      <span className="min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex items-center gap-1.5 truncate text-[15px] font-semibold">
                          {name}
                          {task.relatedIsHot && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Hot lead">
                              <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
                            </svg>
                          )}
                        </span>
                        <span className="block truncate text-[13px] text-text-muted">
                          {task.title}
                          {task.relatedName && task.relatedName !== name ? ` · ${task.relatedName}` : ''}
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li key={task.id} className={`flex h-18 items-center gap-3 border-b border-divider pl-3.5 pr-3 last:border-b-0 ${task.isOverdue ? 'bg-[#FFFBF9]' : ''}`}>
                      {task.relatedType === 'lead' ? (
                        <Link to="/m/leads/$leadId" params={{ leadId: task.relatedId! }} className="flex min-w-0 flex-1 items-center gap-3 no-underline text-ink">
                          {rowInner}
                        </Link>
                      ) : (
                        <span className="flex min-w-0 flex-1 items-center gap-3">{rowInner}</span>
                      )}
                      <a
                        href={task.relatedPhone ? `tel:${task.relatedPhone}` : undefined}
                        aria-label={`Call ${name}`}
                        aria-disabled={!task.relatedPhone}
                        className={`flex size-11 shrink-0 items-center justify-center rounded-full border border-border ${!task.relatedPhone ? 'pointer-events-none opacity-30' : ''} ${i === 0 && task.isOverdue ? '' : 'text-primary'}`}
                        style={i === 0 && task.isOverdue ? undefined : undefined}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
                        </svg>
                      </a>
                      <a
                        href={task.relatedEmail ? `mailto:${task.relatedEmail}` : undefined}
                        aria-label={`Email ${name}`}
                        aria-disabled={!task.relatedEmail}
                        className={`flex size-11 shrink-0 items-center justify-center rounded-full ${!task.relatedEmail ? 'pointer-events-none opacity-30 border border-border' : 'bg-primary-soft text-primary'}`}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        )}
      </main>

      <button
        type="button"
        aria-label="Quick add"
        onClick={() => setQuickAddOpen(true)}
        className="absolute right-4 flex size-14 items-center justify-center rounded-2xl bg-primary shadow-[0_8px_24px_rgba(46,71,214,0.32),0_2px_6px_rgba(22,24,29,0.1)]"
        style={{ bottom: '5rem' }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      <MobileBottomNav active="today" tasksBadge={dashboard?.tasksDueToday} />
      {quickAddOpen && <QuickLogSheet onClose={() => setQuickAddOpen(false)} />}
    </div>
  );
}
